<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import GroupQuestionRow from '@/components/GroupQuestionRow.vue'
import { groupModule } from '@/store/group/module'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import { stripHtmlTags } from '@/utils'
import { analyticsModule } from '@/store/analytics/module'
import type { TGroupQuestionRow } from '@/store/types'

const {
    Table,
    Button: PocketButton,
    EmptyState,
} = UIKit

const route = useRoute()
const router = useRouter()

const questionTableColumns = [{
    name: 'Question',
    propName: 'question',
    isSortDisabled: true,
    styles: {
        width: '560px',
    },
}, {
    name: 'Answered',
    propName: 'numStudentsAnswered',
    isSortDisabled: true,
}, {
    name: 'Correct',
    propName: 'percentStudentsCorrect',
    isSortDisabled: true,
}, {
    name: 'Flagged',
    propName: 'numStudentsFlagged',
    isSortDisabled: true,
    styles: {
        width: '67px',
    },
}]

const groupStats = computed(() => statsModule.getters.getCurrentGroupAggStats())

const isLoading = computed(() => groupStats.value.isLoading)

const groupQuestionStats = computed(() => {
    return groupStats.value.questionStats.filter(q => q.answers.length > 0)
})

const questionAnsweredStats = (serial: string) => {
    const groupAnsweredQuestionStats = groupQuestionStats.value.find(q => q.serial === serial
        && !q.isMockQuestion)
    const numStudentsCorrect = groupAnsweredQuestionStats?.correctAnswerCount || 0
    const numStudentsAnswered = groupAnsweredQuestionStats?.totalAnswers || 0
    return {
        numStudentsAnswered,
        percentStudentsCorrect: Math.round((numStudentsCorrect / numStudentsAnswered) * 100) || 0,
    }
}

const groupFlaggedQuestions = computed(() => {
    const flaggedQuestions: TGroupQuestionRow[] = []
    const currentGroupFlaggedQuestions = groupStats.value.questionStats.filter(q => q.numStudentsFlagged > 0)
    if (currentGroupFlaggedQuestions) {
        currentGroupFlaggedQuestions.forEach(question => {
            if (!question.isMockQuestion) {
                const questionAnsweredStatsResult = questionAnsweredStats(question.serial)
                const questionPrompt = stripHtmlTags(question.prompt)
                const rowQuestion = {
                    id: Math.random(),
                    serial: question.serial,
                    subject: question.subject,
                    prompt: questionPrompt,
                    numStudentsAnswered: questionAnsweredStatsResult.numStudentsAnswered,
                    percentStudentsCorrect: questionAnsweredStatsResult.percentStudentsCorrect,
                    numStudentsFlagged: question.numStudentsFlagged,
                    tabIndex: 0,
                    scenarioPartLabel: question.scenarioPartLabel,
                }
                flaggedQuestions.push(rowQuestion)
            }
        })
    }
    return flaggedQuestions
})

const sortGroupFlaggedQuestions = computed(() => {
    if (groupFlaggedQuestions.value.length > 1) {
        return groupFlaggedQuestions.value.slice()
            .sort((a, b) => a.numStudentsFlagged > b.numStudentsFlagged ? -1 : 1)
            .slice(0, 3)
    }
    return groupFlaggedQuestions.value
})

const viewAllFlaggedQuestion = () => {
    router.push({ name: 'group-content', query: { tab: 'flagged' } })

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        buttonContext: 'Group Stats Flagged Questions View All Button',
    })
}

const rowClicked = (row: { id: number }) => {
    const clickedQuestion = groupFlaggedQuestions.value.find(q => {
        return q.id === row.id
    })

    if (clickedQuestion?.serial) {
        router.push({
            name: route.name as string,
            params: { ...route.params },
            query: {
                questionSerial: clickedQuestion?.serial,
                type: 'flagged',
            },
        })
        analyticsModule.actions.amplitudeTrack('List Item Clicked', {
            listItemContext: 'Group Stats Flagged Question',
            listItemValue: clickedQuestion.serial,
        })
    }
}

onBeforeRouteUpdate((to, from, next) => {
    groupModule.actions.updateCurrentGroupId(to.params.groupId as string)
    next()
})

onMounted(async () => {
    const routeGroupId = route.params.groupId
    await Promise.all([
        groupModule.actions.updateCurrentGroupId(routeGroupId as string),
        studentModule.actions.updateCurrentStudentId(null),
    ])
})
</script>

<template>
    <div class="group-question-table">
        <div
            v-if="sortGroupFlaggedQuestions.length"
            class="group-question-table__group-flagged-questions"
        >
            <Table
                class="group-question-table__table"
                theme="open"
                :rows="sortGroupFlaggedQuestions"
                :columns="questionTableColumns"
                :show-header="false"
                :table-content-styles="{
                    boxSizing: 'none',
                }"
                :table-column-label-styles="{
                    margin: '0 auto',
                    paddingRight: '18px',
                    display: 'flex',
                    fontSize: '12px',
                    color: '#587183',
                }"
                :table-row-styles="{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px 0 12px 12px',
                    cursor: 'pointer',
                }"
                @rowClicked="rowClicked({id: $event.id as number})"
            >
                <template #tableCellValue="{ row, column }">
                    <GroupQuestionRow
                        v-if="sortGroupFlaggedQuestions.length"
                        class="group-question-table__flagged-questions-row"
                        :row="row"
                        :column="column"
                    />
                </template>
            </Table>
            <PocketButton
                class="group-question-table__view-flagged-questions"
                type="secondary"
                @click="viewAllFlaggedQuestion"
            >
                View All Flagged Questions
            </PocketButton>
        </div>
        <div
            v-if="!sortGroupFlaggedQuestions.length && !isLoading"
            class="group-question-table__no-flagged-header"
        >
            <div>
                Questions
            </div>
            <div>
                No. of Students Flagged
            </div>
        </div>
        <div
            v-if="!sortGroupFlaggedQuestions.length && !isLoading"
            class="group-question-table__no-flagged-questions"
        >
            <EmptyState
                class="group-question-table__empty-state"
                state="flagged"
                title="0 Flagged Questions"
                message="Once students flag questions you will be able to see them here."
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.group-question-table {
    &__view-flagged-questions {
        padding: 16px 12px;
    }

    &__no-flagged-header {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: $slate;
        letter-spacing: 0.22px;
        line-height: 14px;
        padding-left: 12px;
        padding-right: 12px;
    }

    &__no-flagged-questions {
        margin: auto;
        text-align: center;
        margin-bottom: 140px;
    }

    &__empty-state {
        margin: 32px auto 14px auto;
    }
}
</style>
